import React, { useState } from 'react';
import { 
  Button, 
  Form, 
  Header, 
  SpaceBetween,
  Container,
  FormField,
  Input,
  Alert,
  Link,
  Box,
  Grid,
  ColumnLayout
} from '@cloudscape-design/components';
import { submissionsApi } from 'api/Submission';
import { useAuth } from './AuthContext';


export function SignInHeader() {
  return (
    <Header
      variant="h1"
      description="Sign in to access your account and manage your submissions."
    >
      Sign In
    </Header>
  );
}

function SignInActions({ onSubmitClick, isLoading, showOtp, onResendOtp }) {
  return (
    <SpaceBetween direction="horizontal" size="xs">
      {showOtp && (
        <Link variant="secondary" onFollow={onResendOtp}>
          Resend OTP
        </Link>
      )}
      <Button 
        variant="primary" 
        onClick={onSubmitClick}
        loading={isLoading}
        data-testid="signin-button"
      >
        {showOtp ? 'Verify OTP' : 'Sign In'}
      </Button>
    </SpaceBetween>
  );
}

export default function EnhancedSignInPage() {
  const { login } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    otp: '',
  });
  
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    otp: '',
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [showOtp, setShowOtp] = useState(false);
  const [appId, setAppId] = useState(null);
  const [otpSent, setOtpSent] = useState(false);

  const validateInitialForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateOtp = () => {
    const newErrors = {};
    
    if (!formData.otp) {
      newErrors.otp = 'OTP is required';
    } else if (formData.otp.length < 6) {
      newErrors.otp = 'OTP must be 6 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async () => {
    if (!validateInitialForm()) {
      return;
    }

    setIsLoading(true);
    setErrorText(null);

    try {
      const response = await submissionsApi.sendOtp({
        email: formData.email,
        password: formData.password
      });
      
      setAppId(response.app_id);
      setShowOtp(true);
      setOtpSent(true);
      // Clear any previous OTP
      setFormData(prev => ({ ...prev, otp: '' }));
      setErrors(prev => ({ ...prev, otp: '' }));
    } catch (error) {
      setErrorText(error.message);
      setShowOtp(false);
      setAppId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignIn = async () => {
    if (!validateInitialForm()) {
      return;
    }

    setIsLoading(true);
    setErrorText(null);

    try {
      // First attempt to authenticate with just email and password
      const response = await submissionsApi.verifyOtpAndAuthenticate({
        email: formData.email,
        password: formData.password
      });

      // If successful, log in the user
      login(response);
      window.location.href = '/my-applications';
    } catch (error) {
      // If authentication fails, send OTP
      try {
        const otpResponse = await submissionsApi.sendOtp({
          email: formData.email,
          password: formData.password
        });
        
        setAppId(otpResponse.app_id);
        setShowOtp(true);
        setOtpSent(true);
        // Clear any previous OTP
        setFormData(prev => ({ ...prev, otp: '' }));
        setErrors(prev => ({ ...prev, otp: '' }));
        setErrorText('An OTP has been sent to your email for verification');
      } catch (otpError) {
        setErrorText(otpError.message);
        setShowOtp(false);
        setAppId(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!validateOtp()) {
      return;
    }

    setIsLoading(true);
    setErrorText(null);

    try {
      const response = await submissionsApi.verifyOtpAndAuthenticate({
        email: formData.email,
        password: formData.password,
        otp: formData.otp,
        app_id: appId
      });

      login(response);
      window.location.href = '/my-applications';
    } catch (error) {
      setErrorText(error.message);

      if (error.message.includes('Invalid OTP')) {
        setFormData(prev => ({ ...prev, otp: '' }));
      }

      if (error.message.includes('Invalid credentials')) {
        setFormData({ email: '', password: '', otp: '' });
        setShowOtp(false);
        setAppId(null);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!validateInitialForm()) {
      return;
    }
    
    setIsLoading(true);
    setErrorText(null);
    
    try {
      const response = await submissionsApi.sendOtp({
        email: formData.email,
        password: formData.password
      });
      setAppId(response.app_id);
      setFormData(prev => ({ ...prev, otp: '' }));
      setErrors(prev => ({ ...prev, otp: '' }));
      setErrorText('OTP has been resent to your email');
    } catch (error) {
      setErrorText(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (showOtp) {
      handleVerifyOtp();
    } else {
      handleSignIn(); // Changed from handleSendOtp to handleSignIn
    }
  };

  return (
    <div style={{ 
      height: '60vh', 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      backgroundColor: '#ffffff'
    }}>
      <Box
        padding="l"
        margin="xxl"
        variant="awsui-key-rotation"
        style={{ 
          width: '80%', 
          maxWidth: '1000px',
          minHeight: '500px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          backgroundColor: 'white',
          overflow: 'hidden'
        }}
      >
        <Grid
          gridDefinition={[
            { colspan: { default: 12, xxs: 12, xs: 6 } },
            { colspan: { default: 12, xxs: 12, xs: 6 } }
          ]}
        >
          {/* Left Side - Sign In Form */}
          <div style={{ 
            padding: '1.5rem', 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center',
            height: '100%'
          }}>
            <form onSubmit={handleSubmit}>
              <Form
                header={<SignInHeader />}
                actions={
                  <SignInActions 
                    onSubmitClick={handleSubmit} 
                    isLoading={isLoading} 
                    showOtp={showOtp}
                    onResendOtp={handleResendOtp}
                  />
                }
              >
                <Container>
                  <SpaceBetween size="l">
                    {errorText && (
                      <Alert 
                        type={errorText.includes('has been resent') || errorText.includes('has been sent') ? 'success' : 'error'}
                        dismissible
                        onDismiss={() => setErrorText(null)}
                      >
                        {errorText}
                      </Alert>
                    )}
                    
                    <FormField
                      label="Email"
                      errorText={errors.email}
                    >
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={({ detail }) => 
                          setFormData(prev => ({ ...prev, email: detail.value }))
                        }
                        placeholder="Enter your email"
                        data-testid="email-input"
                        disabled={showOtp}
                      />
                    </FormField>

                    <FormField
                      label="Password"
                      errorText={errors.password}
                    >
                      <Input
                        type="password"
                        value={formData.password}
                        onChange={({ detail }) => 
                          setFormData(prev => ({ ...prev, password: detail.value }))
                        }
                        placeholder="Enter your password"
                        data-testid="password-input"
                        disabled={showOtp}
                      />
                    </FormField>

                    {showOtp && (
                      <FormField
                        label="One-Time Password (OTP)"
                        errorText={errors.otp}
                        description="Please enter the 6-digit code sent to your email"
                      >
                        <Input
                          type="text"
                          value={formData.otp}
                          onChange={({ detail }) => {
                            const value = detail.value.replace(/[^0-9]/g, '').slice(0, 6);
                            setFormData(prev => ({ ...prev, otp: value }));
                          }}
                          placeholder="Enter 6-digit OTP"
                          data-testid="otp-input"
                        />
                      </FormField>
                    )}
 
                  </SpaceBetween>
                </Container>
              </Form>
            </form>
          </div>

          <div style={{ 
            backgroundColor: '',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '2rem',
            height: '100%',
            borderRadius: '0 8px 8px 0'
          }}>
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%'
            }}>
              {/* Replace with your actual logo */}
              <div style={{ 
                width: '300px', 
                height: '200px', 
                background: 'rgba(255, 255, 255)',
                borderRadius: '12px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '1.5rem',
                padding: '15px',
              }}>
                <img src="https://cirrusgo-logo.s3.eu-west-1.amazonaws.com/logo.svg" alt="Company Logo" style={{ width: '100%', height: '100%' }} />
              </div>
              
              <p style={{ 
                marginTop: '1rem', 
                textAlign: 'center',
                maxWidth: '80%',
                color: 'black',
                fontSize: '1.1rem'
              }}>
                Sign in to access your Eduscribe account
              </p>
            </div>
          </div>
        </Grid>
      </Box>
    </div>
  );
}